import API_URLS from '../../config/apiConfig';
import { apiGet } from '../../utils/apiUtils';
import type { Store, StoreOwner, StoreOwnerDetailsResponse, StoreOwnersListResponse, StoresListResponse } from '../../types/services/auth';

export const getStores = async (params?: {
  subscription_status?: string | null;
  store_status?: string | null;
  search?: string | null;
  get_all?: boolean;
  page?: number;
  page_size?: number;
}): Promise<StoresListResponse> => {
  let userDetails: any = localStorage.getItem('userDetails');
  userDetails = userDetails ? JSON.parse(userDetails) : null;

  const getAll = params?.get_all;
  let url = '';
  if (getAll) {
    url = API_URLS.GET_STORES;
  } else if (userDetails?.is_super_admin) {
    url = API_URLS.GET_ADMIN_STORES;
  } else {
    url = API_URLS.GET_STORES;
  }

  const searchParams = new URLSearchParams();
  const subscriptionStatus = params?.subscription_status;
  const storeStatus = params?.store_status;
  const search = params?.search;
  const page = params?.page;
  const pageSize = params?.page_size;

  if (subscriptionStatus && subscriptionStatus !== 'all') {
    searchParams.set('subscription_status', subscriptionStatus);
  }
  if (storeStatus) {
    searchParams.set('store_status', storeStatus);
  }
  if (search) {
    searchParams.set('search', search);
  }
  if (getAll) {
    searchParams.set('get_all', 'true');
  }
  if (page && page > 0) {
    searchParams.set('page', String(page));
  }
  if (pageSize && pageSize > 0) {
    searchParams.set('page_size', String(pageSize));
  }

  const queryString = searchParams.toString();
  const finalUrl = queryString ? `${url}?${queryString}` : url;

  const accessToken = localStorage.getItem('accessToken');
  const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
  const data = await apiGet(finalUrl, headers ? { headers } : undefined);

  const anyData: any = data as any;
  const stores: Store[] =
    Array.isArray(anyData?.stores) ? anyData.stores
      : Array.isArray(anyData) ? anyData
        : [];

  const total_stores: number = typeof anyData?.total_stores === 'number'
    ? anyData.total_stores
    : stores.length;

  const pageNum: number = typeof anyData?.page === 'number' ? anyData.page : (params?.page || 1);
  const pageSizeNum: number = typeof anyData?.page_size === 'number'
    ? anyData.page_size
    : (params?.page_size || stores.length || 10);

  const totalPages: number = typeof anyData?.total_pages === 'number'
    ? anyData.total_pages
    : Math.max(1, Math.ceil(total_stores / (pageSizeNum || 1)));

  const hasNext: boolean = typeof anyData?.has_next === 'boolean'
    ? anyData.has_next
    : pageNum < totalPages;

  const hasPrev: boolean = typeof anyData?.has_previous === 'boolean'
    ? anyData.has_previous
    : pageNum > 1;

  return {
    total_stores,
    stores,
    page: pageNum,
    page_size: pageSizeNum,
    total_pages: totalPages,
    has_next: hasNext,
    has_previous: hasPrev,
  };
};

export const getStoreOwners = async (params?: {
  page?: number;
  page_size?: number;
  search?: string | null;
  include_without_stores?: boolean;
  active_subscription_only?: boolean;
  exclude_active_subscriptions?: boolean;
  phone_verified_only?: boolean;
}): Promise<StoreOwnersListResponse> => {
  const searchParams = new URLSearchParams();
  const page = params?.page;
  const pageSize = params?.page_size;
  const search = params?.search;
  const includeWithoutStores = params?.include_without_stores;
  const activeSubscriptionOnly = params?.active_subscription_only;
  const excludeActiveSubscriptions = params?.exclude_active_subscriptions;
  const phoneVerifiedOnly = params?.phone_verified_only;

  if (page && page > 0) {
    searchParams.set('page', String(page));
  }
  if (pageSize && pageSize > 0) {
    searchParams.set('page_size', String(pageSize));
  }
  if (search && search.trim()) {
    searchParams.set('search', search.trim());
  }
  if (typeof includeWithoutStores === 'boolean') {
    searchParams.set('include_without_stores', String(includeWithoutStores));
  }
  if (typeof activeSubscriptionOnly === 'boolean') {
    searchParams.set('active_subscription_only', String(activeSubscriptionOnly));
  }
  if (typeof excludeActiveSubscriptions === 'boolean') {
    searchParams.set('exclude_active_subscriptions', String(excludeActiveSubscriptions));
  }
  if (typeof phoneVerifiedOnly === 'boolean') {
    searchParams.set('phone_verified_only', String(phoneVerifiedOnly));
  }

  const queryString = searchParams.toString();
  const finalUrl = queryString
    ? `${API_URLS.GET_STORE_OWNERS}?${queryString}`
    : API_URLS.GET_STORE_OWNERS;

  const data = await apiGet(finalUrl);
  const anyData: any = data || {};
  const owners: StoreOwner[] = Array.isArray(anyData?.owners) ? anyData.owners : [];

  const totalOwners = typeof anyData?.total_owners === 'number'
    ? anyData.total_owners
    : owners.length;
  const pageNum = typeof anyData?.page === 'number' ? anyData.page : (page || 1);
  const pageSizeNum = typeof anyData?.page_size === 'number'
    ? anyData.page_size
    : (pageSize || owners.length || 20);
  const totalPages = typeof anyData?.total_pages === 'number'
    ? anyData.total_pages
    : Math.max(1, Math.ceil(totalOwners / Math.max(1, pageSizeNum)));

  return {
    total_owners: totalOwners,
    owners,
    page: pageNum,
    page_size: pageSizeNum,
    total_pages: totalPages,
    has_next: typeof anyData?.has_next === 'boolean' ? anyData.has_next : pageNum < totalPages,
    has_previous: typeof anyData?.has_previous === 'boolean' ? anyData.has_previous : pageNum > 1,
  };
};

export const getStoreOwnerStores = async (ownerId: string): Promise<StoreOwnerDetailsResponse> => {
  const data = await apiGet(API_URLS.GET_STORE_OWNER_STORES(ownerId));
  const anyData: any = data || {};

  return {
    owner_id: anyData?.owner_id || ownerId,
    name: anyData?.name || '',
    phone_number: anyData?.phone_number || '',
    email_address: anyData?.email_address || '',
    registration_date: anyData?.registration_date || '',
    state_code: anyData?.state_code || '',
    state_name: anyData?.state_name || '',
    no_of_stores: typeof anyData?.no_of_stores === 'number' ? anyData.no_of_stores : 0,
    stores: Array.isArray(anyData?.stores) ? anyData.stores : [],
  };
};
